import { useMemo } from 'react';

import type { InteractiveSubActivityFragment } from '../../lesson-builder.graphql.generated';
import SubActivityView from '../../components/sub-activity/sub-activity-view';
import ProgressIndicator from 'approot/shared/progress-bar/progress-indicator';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import { green6, grey6 } from 'approot/shared/styles/colours.style';
import { useActivityBuilderPresentContext } from '../../present/activity-builder-present.context';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import { ClassroomAnswerInstruction } from '../classroom-answer.style';
import {
  ClassResponseListContent,
  ClassResponseListItem,
  ClassResponseListItemText,
} from '../class-responses.style';
import Bouncer from 'approot/shared/bouncer/bouncer';
import {
  ClassAnswerNoStudents,
  ClassAnswerNoAnswers,
} from '../class-answer-empty';
import { MEDIA_MD, useMediaQuery } from 'approot/shared/style';
import { useGetClassStudents } from '../classroom-student.hooks';
import { prepareClassResults } from './interactive-response-class-responses.utils';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';

type Props = {
  componentId: string;
  subActivityProperties: InteractiveSubActivityFragment | null;
};

const InteractiveResponseClassResponses = ({
  componentId,
  subActivityProperties,
}: Props) => {
  const { userClassId } = useActivityBuilderPresentContext();

  const isMediaUpMd = useMediaQuery(MEDIA_MD);

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const { data, error: studentAnswersError } =
    useGetStudentAnswersPollingFallback({
      userClassId: Number(userClassId),
      componentId,
    });

  const classResponses = useMemo(() => {
    return prepareClassResults(data?.getClassStudentsAnswers || []);
  }, [data?.getClassStudentsAnswers]);

  return (
    <>
      {studentsLoading || !data ? (
        <Bouncer />
      ) : studentAnswersError || studentsError ? (
        <ErrorMessageActionable ariaPage="Class Responses" paddingTop={0} />
      ) : studentsInClass.length === 0 ? (
        <ClassAnswerNoStudents />
      ) : data?.getClassStudentsAnswers.length === 0 ? (
        <ClassAnswerNoAnswers />
      ) : (
        <ClassroomAnswerInstruction>
          <SubActivityView subActivityDetails={subActivityProperties}>
            <BoxList>
              <ClassResponseListItem>
                <Box $gap={12} $alignItems="center">
                  <ClassResponseListContent>
                    <ClassResponseListItemText>
                      Correct. Well done! 🎉
                      <Box $gap={4} $alignItems="center">
                        <ActivityBuilderInlineSvg
                          id="icon_correct"
                          width={isMediaUpMd ? 32 : 24}
                          height={isMediaUpMd ? 32 : 24}
                        />

                        <span>{classResponses.correct}</span>
                      </Box>
                    </ClassResponseListItemText>
                    <ProgressIndicator
                      animate
                      progress={classResponses.correct}
                      total={studentsInClass.length}
                      colour={green6}
                      height={isMediaUpMd ? 24 : 16}
                    />
                  </ClassResponseListContent>
                </Box>
              </ClassResponseListItem>
              <ClassResponseListItem>
                <Box $gap={12} $alignItems="center">
                  <ClassResponseListContent>
                    <ClassResponseListItemText>
                      Incorrect. Don’t give up!
                      <Box $gap={4} $alignItems="center">
                        <span>{classResponses.incorrect}</span>
                      </Box>
                    </ClassResponseListItemText>
                    <ProgressIndicator
                      animate
                      progress={classResponses.incorrect}
                      total={studentsInClass.length}
                      colour={grey6}
                      height={isMediaUpMd ? 24 : 16}
                    />
                  </ClassResponseListContent>
                </Box>
              </ClassResponseListItem>
            </BoxList>
          </SubActivityView>
        </ClassroomAnswerInstruction>
      )}
    </>
  );
};

export default InteractiveResponseClassResponses;
